import { PersonalInfoEditorViewModel } from '@/features/tax-profile/individual-profiles/PersonalInfoEditorViewModel'
import { action, observable, runInAction, computed } from 'mobx'
import type { SpousalProfileVersion } from '@/features/tax-profile/individual-profiles/SpousalProfile'
import { pattern, required, validationContext } from 'validx'
import {
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
  validateObject,
} from '@/utils/validx-util'
import type { IndividualProfileStore } from '@/features/tax-profile/individual-profiles/IndividualProfileStore'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { task } from 'mobx-task'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'

export interface SpousalProfileEditorViewModelOptions {
  /**
   * Whether the editor is in read-only mode.
   */
  isReadOnly?: () => boolean

  /**
   * Called when the profile has been saved.
   *
   * @param profile
   */
  onSaved(profile: SpousalProfileVersion): Promise<unknown>
}

/**
 * Editor for a spousal profile.
 */
export class SpousalProfileEditorViewModel {
  readonly personalInfoVm = new PersonalInfoEditorViewModel()

  /**
   * Underlying modal state, for when the editor is displayed in a modal.
   */
  readonly modal = new ModalViewModel()

  @observable accessor occupation = ''
  @observable accessor email = ''
  @observable accessor isNew = true
  @observable accessor isPrefilledUsingLatest = false

  /**
   * Whether the editor is in read-only mode.
   */
  @computed get isReadOnly() {
    return this._options.isReadOnly?.() ?? false
  }

  readonly validation = validationContext(this, {
    personalInfoVm: [validateObject()],
    email: [
      required('Email is required'),
      pattern('email', 'Must be a valid email'),
    ],
    occupation: [
      // Occupation is optional for spousal profiles
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'Occupation must not exceed 100 characters'),
    ],
  })

  constructor(
    private readonly _individualProfileStore: IndividualProfileStore,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _options: SpousalProfileEditorViewModelOptions,
  ) {}

  /**
   * Resets the view model to the given profile or to a blank state.
   */
  @action.bound
  reset(profile?: SpousalProfileVersion | null) {
    this.isNew = !profile
    this.isPrefilledUsingLatest = false // reset the flag
    this.personalInfoVm.reset(profile?.personalInfo)
    this.occupation = profile?.occupation ?? ''
    this.email = profile?.contactInfo?.email ?? ''

    this.validation.reset()
  }

  /**
   * Opens the dialog, optionally with the given profile.
   */
  @action.bound
  showDialog(profile: SpousalProfileVersion | null = null) {
    this.reset(profile)

    if (this.isNew) {
      // Try to fetch a fallback.
      void this.fetchFallback()
    }

    this.modal.show()
  }

  /**
   * Closes the dialog.
   */
  @action.bound
  close() {
    this.modal.close()
  }

  /**
   * Fetches the latest profile to suggest as a fallback.
   */
  fetchFallback = task.resolved(async (signal?: AbortSignal) => {
    const latest =
      await this._individualProfileStore.fetchLatestSpousalProfileVersion(
        signal,
      )
    signal?.throwIfAborted()
    runInAction(() => {
      this.reset(latest)
      this.isPrefilledUsingLatest = !!latest

      // Treat it as a new profile.
      this.isNew = true
    })
  })

  /**
   * Saves the profile and closes the dialog.
   */
  save = task.resolved(async () => {
    this.validation.reset().validate()
    if (!this.validation.isValid) {
      return
    }

    await this._individualProfileStore
      .saveSpousalProfileVersion({
        personalInfo: this.personalInfoVm.toPersonalInfo(),
        occupation: this.occupation,
        email: this.email,
      })
      .then(this._options.onSaved)
      .then(() => this.close())
      .catch(this._createSnackbarErrorHandler('Failed to save spousal profile'))
  })
}
